import { SALVAR_USUARIO } from '@/modules/usuarios-edicao/data/salvar-usuario'
import { CARGOS_BY_USUARIO_QUERY, CARGOS_QUERY } from '../data/cargos'
import { INFO_USUARIO_QUERY } from '../data/info-usuario'
import { USUARIO_DOMINIO_QUERY } from '@/modules/usuarios-edicao/data/usuario-dominio-query'
import { USUARIO_QUERY } from '@/modules/usuarios-core/data/usuario-query'
import { USUARIOS_PORTAL_PROPERTIES_QUERY } from '@/modules/usuarios-edicao/data/usuarios-portal-properties'
import { useMutation, useQuery } from '@apollo/client'
import {
  CargosByUsuarioQuery,
  CargosByUsuarioQueryVariables,
  CargosQuery,
  CargosQueryVariables,
  InfoUsuarioQueryQuery,
  InfoUsuarioQueryQueryVariables,
  SalvarUsuarioMutation,
  SalvarUsuarioMutationVariables,
  UsuarioDominioQueryQuery,
  UsuarioDominioQueryQueryVariables,
  UsuarioQueryQuery,
  UsuarioQueryQueryVariables,
  UsuariosPortalPropertiesQueryQuery,
  UsuariosPortalPropertiesQueryQueryVariables
} from '@/core/types/graphql'
import { useConfirmModal } from '@/core/context/confirm-modal-context'
import { t } from '@lingui/core/macro'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { USUARIOS_PAGED_QUERY } from '../../usuarios-listagem/data/usuarios-paged-query'

export const useUsuarioEdicaoQueryHandler = () => {
  const { askUser } = useConfirmModal()
  const { id } = useParams()
  const { data: usuarioData, loading: usuarioLoading } = useQuery<
    UsuarioQueryQuery,
    UsuarioQueryQueryVariables
  >(USUARIO_QUERY, {
    variables: id ? { id } : undefined,
    skip: !id,
    fetchPolicy: 'network-only'
  })
  const { data: usuarioDominioData, loading: usuarioDominioLoading } = useQuery<
    UsuarioDominioQueryQuery,
    UsuarioDominioQueryQueryVariables
  >(USUARIO_DOMINIO_QUERY)

  const { data: cargosData, loading: cargosLoading } = useQuery<
    CargosQuery,
    CargosQueryVariables
  >(CARGOS_QUERY, {
    variables: id ? { cargosPrincipais: [id] } : undefined,
    skip: !id,
    fetchPolicy: 'network-only'
  })
  const { data: cargosByUsuarioData, loading: cargosByUsuarioLoading } =
    useQuery<CargosByUsuarioQuery, CargosByUsuarioQueryVariables>(
      CARGOS_BY_USUARIO_QUERY,
      {
        variables: id ? { usuarioId: id } : undefined,
        skip: !id,
        fetchPolicy: 'no-cache'
      }
    )
  const { data: infoUsuarioData, loading: infoUsuarioLoading } = useQuery<
    InfoUsuarioQueryQuery,
    InfoUsuarioQueryQueryVariables
  >(INFO_USUARIO_QUERY, {
    variables: id ? { usuarioId: id } : undefined,
    skip: !id,
    fetchPolicy: 'network-only'
  })
  const {
    data: usuariosPortalPropertiesData,
    loading: usuariosPortalPropertiesLoading
  } = useQuery<
    UsuariosPortalPropertiesQueryQuery,
    UsuariosPortalPropertiesQueryQueryVariables
  >(USUARIOS_PORTAL_PROPERTIES_QUERY)

  const [salvarUsuarioMutation] = useMutation<
    SalvarUsuarioMutation,
    SalvarUsuarioMutationVariables
  >(SALVAR_USUARIO, {
    refetchQueries: [USUARIO_QUERY, USUARIOS_PAGED_QUERY]
  })

  const salvarUsuario = useCallback(
    (usuario, cargos, infos, successMessage, onClose) => {
      askUser({
        onConfirm: async () => {
          try {
            await salvarUsuarioMutation({
              variables: { usuario, cargos, infos }
            })
            onClose()
          } catch (error) {
            console.error('Erro ao salvar usuario:', error)
            throw error
          }
        },
        title: t`Deseja salvar o usuario ${usuario.nome}?`,
        confirmLabel: t`Salvar`,
        successMessage: `${successMessage}`,
        errorMessage: t`Não foi possível salvar o usuario`
      })
    },
    [salvarUsuarioMutation, askUser]
  )

  const allDataLoaded =
    !usuarioDominioLoading &&
    !usuarioLoading &&
    !infoUsuarioLoading &&
    !cargosByUsuarioLoading &&
    !usuariosPortalPropertiesLoading &&
    !cargosLoading

  const { produtos, marcas, tiposUsuario, grupos, dealers } =
    usuarioDominioData ?? {}
  const cargos = cargosByUsuarioData?.cargos ?? []
  const { infoUsuario } = infoUsuarioData ?? {}
  const {
    tiposUsuarioDocumentoObrigatorio = [],
    tiposUsuarioInformarCargo = [],
    tiposUsuarioInformarGestao = []
  } = usuariosPortalPropertiesData?.usuariosPortalProperties ?? {}

  const initialValuesMemoized = useMemo(() => {
    return {
      produtos,
      marcas,
      tiposUsuario,
      grupos,
      dealers,
      cargos,
      infoUsuario
    }
  }, [usuarioDominioData, cargosByUsuarioData, infoUsuarioData])

  const tiposMap = useMemo(() => {
    const dealerGrupoMap = {}
    const tiposUsuarioMap = []
    const tipoAcessoMap = {}
    dealers?.forEach(({ id, grupo: { id: grupoId } }) => {
      dealerGrupoMap[id] = grupoId
    })

    tiposUsuario?.forEach(tipo => {
      tiposUsuarioMap.push({
        ...tipo,
        description: `${tipo?.description}`
      })
    })

    for (let i = 0; i < produtos?.length; i++) {
      const produto = produtos[i]

      for (const tipoAcesso of produto.tiposAcesso) {
        tipoAcessoMap[tipoAcesso.id] = tipoAcesso
      }
    }
    return {
      dealerGrupoMap,
      tipoAcessoMap,
      tiposUsuarioMap
    }
  }, [dealers, tiposUsuario, produtos])

  return {
    usuarioData,
    usuarioDominioData,
    cargosData,
    cargosByUsuarioData,
    infoUsuarioData,
    usuariosPortalPropertiesData,
    salvarUsuario,
    allDataLoaded,
    produtos,
    marcas,
    tiposUsuario,
    cargos,
    infoUsuario,
    tiposUsuarioDocumentoObrigatorio,
    tiposUsuarioInformarCargo,
    tiposUsuarioInformarGestao,
    grupos,
    dealers,
    tiposMap,
    id,
    initialValuesMemoized
  }
}
